<!--
  Shared editor template for ALL channel editors (types/* and translate/*).
  Per-channel differences are driven by overridable members on
  BaseTemplateEditorComponent: channelLabel / namePlaceholder / emptyMessage /
  heartbeatType and the feature flags enableDragDrop, enableBlockToolbar,
  showResponsysButton, showMetaSection, showTranslateButton, showSaveAsEmpty,
  guardByProcessing.
-->
<div class="card text-center mt-2">
  <div class="card-header d-inline-flex">
    <div *ngIf="templateName; else noName">
      Edit {{ channelLabel }} template
      <span class="font-bold">{{ templateName }}</span>
    </div>
    <ng-template #noName>
      <div>{{ channelLabel }} template</div>
    </ng-template>

    <button
      class="btn btn-secondary d-flex align-items-center ms-auto"
      (click)="goBack()"
    >
      <span class="material-icons me-1" title="Go Back">arrow_left</span>
      <span class="font-medium">Back</span>
    </button>
  </div>
  <div class="card-body">
    <div class="row form-group d-flex justify-content-center">
      <div class="col-12 position-relative w-5 mx-auto">
        <p-autocomplete
          id="templateName"
          name="templateName"
          [(ngModel)]="templateName"
          [suggestions]="suggestions!"
          (completeMethod)="searchTemplate($event)"
          required="true"
          delay="0"
          [placeholder]="namePlaceholder"
          scrollHeight="400px"
          [emptyMessage]="emptyMessage"
          inputStyleClass="search-campaign-autocomplete"
          (onSelect)="setTemplateName($event)"
          optionLabel="name"
          [style]="{ zIndex: '4' }"
        >
          <ng-template let-templateName pTemplate="templateName">
            <div [style.white-space]="'pre-wrap'">
              {{ templateName.hint }}
            </div>
          </ng-template>
        </p-autocomplete>
        <div></div>
      </div>

      <div
        class="d-flex flex-row flex-wrap gap-2 m-3 justify-content-center"
        *ngIf="!isTranslator"
      >
        <div *ngIf="idcustom">
          <span
            *ngIf="!isMyLock()"
            [ngbTooltip]="'Lock the template yourself to delete it.'"
            container="body"
          >
            <button
              type="button"
              class="btn btn-danger"
              (click)="confirmTableDeletion(content)"
              [disabled]="
                (!isMyLock() &&
                  (!guardByProcessing || isTemplateProcToResponsys)) ||
                isCopy ||
                isNew
              "
            >
              Delete
            </button>
          </span>
          <!-- Button without tooltip when enabled -->
          <span *ngIf="isMyLock()">
            <button
              type="button"
              class="btn btn-danger"
              (click)="confirmTableDeletion(content)"
              [disabled]="isNew"
            >
              Delete
            </button>
          </span>
        </div>
        <div *ngIf="idcustom">
          <button
            type="button"
            class="btn btn-primary"
            (click)="outputTextContent(longContent)"
            [disabled]="
              selectedBlocks.length === 0 ||
              (guardByProcessing && isTemplateProcToResponsys)
            "
          >
            Write to file
          </button>
        </div>
        <div *ngIf="idcustom">
          <button
            type="button"
            class="btn btn-secondary"
            (click)="setPreview()"
            [disabled]="
              selectedBlocks.length === 0 ||
              isCopy ||
              (guardByProcessing && isTemplateProcToResponsys)
            "
          >
            Preview
          </button>
        </div>
        <div *ngIf="idcustom">
          <button
            type="button"
            class="btn bg-yellow-500 text-white"
            (click)="copyTemplate()"
            [disabled]="!isMyLock() || isCopy"
          >
            Copy
          </button>
        </div>
        <div *ngIf="idcustom && showTranslateButton">
          <button
            type="button"
            class="btn bg-purple-500 text-white"
            (click)="translateTemplate()"
            [disabled]="
              isApproved ||
              (guardByProcessing && isTemplateProcToResponsys) ||
              isCopy ||
              isNew
            "
          >
            Translate
          </button>
        </div>
      </div>

      <!-- WhatsApp: Meta approval status badges -->
      <div *ngIf="showMetaSection">
        <div
          *ngIf="metaStatus === MetaStatusEnum.APPROVED && templateName"
          class="row justify-content-center"
        >
          <span class="badge rounded-pill text-bg-success col-auto fs-6 p-3">
            This template has been approved by Meta!
          </span>
        </div>
        <div
          *ngIf="metaStatus === MetaStatusEnum.PENDING && templateName"
          class="row justify-content-center"
        >
          <span class="badge rounded-pill text-bg-primary col-auto fs-6 p-3">
            This template is still pending approval!
          </span>
        </div>
        <div
          *ngIf="metaStatus === MetaStatusEnum.REJECTED && templateName"
          class="row justify-content-center"
        >
          <span class="badge rounded-pill text-bg-danger col-auto fs-6 p-3">
            This template was rejected by Meta!
          </span>
        </div>
        <div
          *ngIf="metaStatus === MetaStatusEnum.FAILED && templateName"
          class="row justify-content-center"
        >
          <span class="badge rounded-pill text-bg-danger col-auto fs-6 p-3">
            Template failed while sending for approval or while updating its
            status!
          </span>
        </div>
        <div
          *ngIf="metaStatus === MetaStatusEnum.NEVER_SENT && templateName"
          class="row justify-content-center"
        >
          <span class="badge rounded-pill text-bg-secondary col-auto fs-6 p-3">
            Send the template to Meta!
          </span>
        </div>
      </div>
    </div>

    <!-- START: Modal for getting text output -->
    <ng-template class="modal" #longContent let-outputModal>
      <div class="modal-header">
        <h4 class="modal-title">Template text content</h4>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          (click)="outputModal.dismiss('Cross click')"
        ></button>
      </div>
      <div class="modal-body">
        <div [innerHTML]="contentString"></div>
      </div>
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-primary"
          (click)="
            templateExportService.downloadOutput(contentString, templateName!)
          "
        >
          Download file
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="templateExportService.copyToClipboard(contentString)"
        >
          Copy to clipboard
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          (click)="outputModal.close('Close click')"
        >
          Close
        </button>
      </div>
    </ng-template>

    <ng-template #content let-modal class="modal">
      <div class="modal-header">
        <h4 class="modal-title">Delete {{ templateName }}?</h4>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          (click)="activateMenuStatusModal()"
        ></button>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-danger" (click)="deleteTable()">
          Delete table
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="activateMenuStatusModal()"
        >
          Cancel
        </button>
      </div>
    </ng-template>

    <!-- WhatsApp: confirm send-for-approval modal -->
    <ng-template #whatsappModal let-modal class="modal">
      <div class="modal-header">
        <h4 class="modal-title">Send {{ templateName }} for approval?</h4>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          (click)="activateMenuStatusModal()"
        ></button>
      </div>
      <div class="modal-body">
        If you decide to send this, you won't be able to edit this template
        anymore and it will be sent to Meta!
      </div>
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-danger"
          (click)="onSubmitBlocks(true)"
        >
          Send
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="activateMenuStatusModal()"
        >
          Cancel
        </button>
      </div>
    </ng-template>

    <div *ngIf="!guardByProcessing || !isTemplateProcToResponsys">
      <div class="form-check form-check-inline mt-2">
        <input
          class="form-check-input cursor-pointer"
          type="radio"
          [defaultChecked]="true"
          (change)="onLanguageChange()"
          [(ngModel)]="selectedLanguage"
          id="inlineRadio1"
          name="en"
          value="en"
          [disabled]="isCopy || isTranslator"
        />
        <label class="form-check-label" name="en" for="inlineRadio1">EN</label>
      </div>
      <div class="form-check form-check-inline">
        <input
          class="form-check-input cursor-pointer"
          type="radio"
          (change)="onLanguageChange()"
          [(ngModel)]="selectedLanguage"
          id="inlineRadio2"
          name="hr"
          value="hr"
          [disabled]="isCopy || isTranslator"
        />
        <label class="form-check-label" name="hr" for="inlineRadio2">HR</label>
      </div>
      <div class="form-check form-check-inline">
        <input
          class="form-check-input cursor-pointer"
          type="radio"
          (change)="onLanguageChange()"
          [(ngModel)]="selectedLanguage"
          id="inlineRadio3"
          value="de"
          [disabled]="isCopy || isTranslator"
        />
        <label class="form-check-label" name="de" for="inlineRadio3">DE</label>
      </div>
      <div class="form-check form-check-inline">
        <input
          class="form-check-input cursor-pointer"
          type="radio"
          (change)="onLanguageChange()"
          [(ngModel)]="selectedLanguage"
          id="inlineRadio3"
          name="slo"
          value="slo"
          [disabled]="isCopy || isTranslator"
        />
        <label class="form-check-label" name="slo" for="inlineRadio4"
        >SLO</label
        >
      </div>
      <div class="form-check form-check-inline">
        <input
          class="form-check-input cursor-pointer"
          type="radio"
          (change)="onLanguageChange()"
          [(ngModel)]="selectedLanguage"
          id="inlineRadio3"
          name="it"
          value="it"
          [disabled]="isCopy || isTranslator"
        />
        <label class="form-check-label" name="it" for="inlineRadio5">IT</label>
      </div>
    </div>

    <!-- Block picker -->
    <div
      class="ui-grid ui-grid-responsive ui-fluid mt-3"
      *ngIf="
        enableBlockToolbar &&
        idcustom &&
        isUnlocked() &&
        !isTemplateProcToResponsys
      "
    >
      <div class="ui-grid-row" *ngIf="!isTranslator">
        <div class="ui-grid-col-12">
          <p-select
            #dropdownBlocks
            [options]="blockSelect"
            [(ngModel)]="selectedBlock"
            optionLabel="dropdownName"
            optionValue="id"
            [filter]="true"
            filterBy="dropdownName"
            [showClear]="true"
            placeholder="Select new block"
            [style]="{ width: '25%', zIndex: '3' }"
            [ngModelOptions]="{ standalone: true }"
            name="dropdownBlocks"
            [disabled]="
              (!isNew && lockedBy.username !== currentUser?.username) || isCopy
            "
            resetFilterOnHide="true"
            scrollHeight="25rem"
          >
            <ng-template let-block pTemplate="item">
              <div class="block-item-content" (click)="handleBlockClick(block)">
                {{ block.dropdownName }}
              </div>
            </ng-template>
          </p-select>
        </div>
      </div>
    </div>

    <div
      *ngIf="!isNew && !isTranslator"
      class="d-flex-inline w-full bg-red-200"
    >
      <div *ngIf="selectedLanguage === 'en'" class="mt-2">
        <button
          class="btn btn-success float-start d-flex align-items-center gap-2"
          (click)="connectToCampaign(idcustom)"
          [disabled]="!templateName || templateName.trim() === ''"
        >
          <span>Edit campaign connection</span>
        </button>
      </div>

      <!-- Save to Responsys (email/sms/push/web-push) -->
      <div class="col-md-6 offset-md-6" *ngIf="showResponsysButton">
        <button
          class="responsys-btn btn float-end d-flex align-items-center gap-2"
          (click)="onSubmitBlocks(true)"
          [disabled]="
            !(lockedBy.username !== '' && lockedBy.username !== null) ||
            isTemplateProcToResponsys
          "
        >
          <!-- SVG ikona koverte -->
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M3 6.5V14a1 1 0 001 1h12a1 1 0 001-1V6.5l-7 4.2-7-4.2z"
              fill="#d32f2f"
            />
            <path
              d="M3 6a1 1 0 011-1h12a1 1 0 011 1v.26l-7 4.2-7-4.2V6z"
              fill="#d32f2f"
            />
          </svg>
          <span class="responsys-label">Save to Responsys</span>
        </button>
      </div>

      <!-- Send to Meta for approval (whatsapp) -->
      <div class="col-md-6 offset-md-6" *ngIf="showMetaSection">
        <button
          class="meta-btn btn float-end d-flex align-items-center gap-2"
          (click)="confirmApprovalSend(whatsappModal)"
          [disabled]="
            !(lockedBy.username !== '' && lockedBy.username !== null) ||
            isTemplateProcToResponsys ||
            isApproved
          "
        >
          <i class="bi bi-meta"></i>
          <span class="meta-label">Send to Meta for approval</span>
          <br/>
        </button>
      </div>
    </div>
  </div>
  <div class="col-md-6 offset-md-6 mb-2 pr-3" *ngIf="showSaveAsEmpty">
    <button
      class="btn btn-secondary float-end d-flex align-items-center gap-2"
      (click)="saveAsEmpty()"
      [disabled]="!templateName || templateName.trim() === ''"
    >
      <!-- SVG ikona koverte -->
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
          d="M3 6.5V14a1 1 0 001 1h12a1 1 0 001-1V6.5l-7 4.2-7-4.2z"
          fill="#ffffff"
        />
        <path
          d="M3 6a1 1 0 011-1h12a1 1 0 011 1v.26l-7 4.2-7-4.2V6z"
          fill="#ffffff"
        />
      </svg>
      <span>Save as empty</span>
    </button>
  </div>
</div>

<div id="content-main" class="disable-input">
  <!-- START: Radio menu for view and annotate -->
  <div *ngIf="idcustom" class="justify-content-left mt-2" id="annotate-menu">
    <div class="flex flex-wrap gap-3">
      <div
        class="lock-menu"
        *ngIf="
          !isTranslated &&
          (!isBeingTranslated || isTranslator) &&
          (!guardByProcessing || !isTemplateProcToResponsys) &&
          !isCopy &&
          (!showMetaSection || (!isApproved && metaCanEdit))
        "
      >
        <div
          *ngIf="lockedBy.username !== '' && lockedBy.username !== null"
          class="mr-auto flex flex-inline mt-2"
          [ngClass]="{
            'text-danger': lockedBy.username !== currentUser?.username,
            'text-success': lockedBy.username === currentUser?.username,
          }"
        >
          <i> Template locked by: {{ lockedBy.name || "" }}</i>
          <div>
            <span
              *ngIf="lockedBy.username !== currentUser?.username"
              class="material-icons mr-3 text-danger"
              [ngbTooltip]="'Template is locked'"
            >
              lock
            </span>
            <span
              *ngIf="lockedBy.username === currentUser?.username"
              class="material-icons mr-3 text-success cursor-pointer"
              [ngbTooltip]="'Unlock template'"
              (click)="sendLockedBy()"
            >
              lock
            </span>
          </div>
        </div>

        <div
          *ngIf="lockedBy.username === '' && !isNew"
          class="mr-auto text-primary flex flex-inline mt-2"
        >
          <div>
            <i> Template is unlocked. Lock it for edit. </i>
          </div>
          <div [ngbTooltip]="'Click to lock template'">
            <span
              class="material-icons cursor-pointer mr-3 text-primary"
              (click)="sendLockedBy(currentUser)"
            >
              lock_open
            </span>
          </div>
        </div>
      </div>
      <div *ngIf="isBeingTranslated && !isTranslator" class="text-danger">
        <div>
          <i class="bi bi-chat-square-dots-fill mr-1"></i>
          This template is being translated! You can't edit it now!
        </div>
      </div>
      <div
        *ngIf="guardByProcessing && isTemplateProcToResponsys"
        class="text-danger"
      >
        <div>
          <i class="bi bi-chat-square-dots-fill mr-1"></i>
          This template is being processed! You can't edit it now!
        </div>
      </div>
      <div *ngIf="isCopy" class="text-primary">
        <div>
          <i class="bi bi-chat-square-dots-fill mr-1"></i>
          Scroll to bottom to confirm the copy process!
        </div>
      </div>
      <div
        class="ml-auto flex align-items-center"
        [ngbTooltip]="'Refresh page'"
      >
        <span
          class="material-icons cursor-pointer text-primary"
          (click)="reloadPage()"
        >
          refresh
        </span>
      </div>
    </div>
  </div>

  <!-- END: Radio menu for view and annotate -->
  <div class="card-body">
    <div [class.google-preview-layout]="showSidePreview">
      <form
        (ngSubmit)="onSubmitBlocks()"
        *ngIf="selectedBlocks && selectedBlocks.length > 0"
      >
        <div
          class="form-group"
          cdkDropList
          [cdkDropListDisabled]="!enableDragDrop || isReadonly()"
          (cdkDropListDropped)="drop($event)"
        >
          <div
            *ngFor="let selectedBlock of selectedBlocks"
            cdkDrag
            [cdkDragDisabled]="!enableDragDrop"
          >
            <div class="placeholder" *cdkDragPlaceholder></div>
            <container-element [ngSwitch]="selectedBlock.name">
              <!-- ===== Single-channel blocks ===== -->
              <div *ngSwitchCase="'sms'">
                <app-sms
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-sms>
              </div>
              <div *ngSwitchCase="'whatsapp'">
                <app-whatsapp
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-whatsapp>
              </div>
              <div *ngSwitchCase="'push'">
                <app-push
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-push>
              </div>
              <div *ngSwitchCase="'web-push'">
                <app-web-push
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-web-push>
              </div>
              <div *ngSwitchCase="'rcs'">
                <app-rcs
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-rcs>
              </div>
              <div *ngSwitchCase="'viber'">
                <app-viber
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-viber>
              </div>

              <!-- ===== Google RSA search blocks ===== -->
              <div *ngSwitchCase="'headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-headline>
              </div>

              <div *ngSwitchCase="'description'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-description
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-description>
              </div>

              <div *ngSwitchCase="'rsa-search-image'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-rsa-search-image
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-rsa-search-image>
              </div>

              <div *ngSwitchCase="'sitelink'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-sitelink
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-sitelink>
              </div>

              <div *ngSwitchCase="'rsa-search-final-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-rsa-search-final-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-rsa-search-final-url>
              </div>

              <div *ngSwitchCase="'rsa-search-display-path'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-rsa-search-display-path
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-rsa-search-display-path>
              </div>

              <div *ngSwitchCase="'rsa-search-calls'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-rsa-search-calls
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-rsa-search-calls>
              </div>

              <div *ngSwitchCase="'rsa-search-callouts'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-rsa-search-callouts
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-rsa-search-callouts>
              </div>

              <div *ngSwitchCase="'gen-single-image-cta-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-single-image-cta-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-single-image-cta-text>
              </div>

              <!-- ===== Google Demange Gen Single Image ===== -->
              <div *ngSwitchCase="'gen-single-image-headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-single-image-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-single-image-headline>
              </div>

              <div *ngSwitchCase="'gen-single-image-description'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-single-image-description
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-single-image-description>
              </div>

              <div *ngSwitchCase="'gen-single-image-image'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-single-image-image
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-single-image-image>
              </div>

              <div *ngSwitchCase="'gen-single-image-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-single-image-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-single-image-url>
              </div>

              <!-- No removeBlockEvent: the logo is one per ad, so the block
                   carries no delete button. -->
              <div *ngSwitchCase="'gen-single-image-logo'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-single-image-logo
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                ></app-gen-single-image-logo>
              </div>

              <!-- ===== Google Demand Gen Video ===== -->
              <div *ngSwitchCase="'gen-video-long-headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-video-long-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-video-long-headline>
              </div>

              <div *ngSwitchCase="'gen-video-headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-video-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-video-headline>
              </div>

              <div *ngSwitchCase="'gen-video-description'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-video-description
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-video-description>
              </div>

              <div *ngSwitchCase="'gen-video-youtube-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-video-youtube-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-video-youtube-url>
              </div>

              <div *ngSwitchCase="'gen-video-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-video-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-video-url>
              </div>

              <div *ngSwitchCase="'gen-video-cta-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-gen-video-cta-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-gen-video-cta-text>
              </div>

              <!-- ===== Google Performance Max ===== -->
              <div *ngSwitchCase="'pmax-long-headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-pmax-long-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-pmax-long-headline>
              </div>

              <div *ngSwitchCase="'pmax-headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-pmax-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-pmax-headline>
              </div>

              <div *ngSwitchCase="'pmax-description'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-pmax-description
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-pmax-description>
              </div>

              <div *ngSwitchCase="'pmax-image'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-pmax-image
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-pmax-image>
              </div>

              <div *ngSwitchCase="'pmax-youtube-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-pmax-youtube-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-pmax-youtube-url>
              </div>

              <div *ngSwitchCase="'pmax-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-pmax-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-pmax-url>
              </div>

              <div *ngSwitchCase="'pmax-cta-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-pmax-cta-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-pmax-cta-text>
              </div>

              <div *ngSwitchCase="'pmax-sitelink'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-pmax-sitelink
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-pmax-sitelink>
              </div>

              <!-- ===== Google YouTube Video Ad ===== -->
              <div *ngSwitchCase="'youtube-video-headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-youtube-video-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-youtube-video-headline>
              </div>

              <div *ngSwitchCase="'youtube-video-youtube-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-youtube-video-youtube-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-youtube-video-youtube-url>
              </div>

              <div *ngSwitchCase="'youtube-video-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-youtube-video-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-youtube-video-url>
              </div>

              <div *ngSwitchCase="'youtube-video-cta-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-youtube-video-cta-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-youtube-video-cta-text>
              </div>

              <div *ngSwitchCase="'youtube-video-sitelink'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-youtube-video-sitelink
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-youtube-video-sitelink>
              </div>

              <!-- ===== Meta Single Image Ad ===== -->
              <div *ngSwitchCase="'meta-single-image-headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-single-image-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-single-image-headline>
              </div>

              <div *ngSwitchCase="'meta-single-image-primary-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-single-image-primary-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-single-image-primary-text>
              </div>

              <div *ngSwitchCase="'meta-single-image-cta-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-single-image-cta-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-single-image-cta-text>
              </div>

              <div *ngSwitchCase="'meta-single-image-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-single-image-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-single-image-url>
              </div>

              <div *ngSwitchCase="'meta-single-image-account-type'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-single-image-account-type
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-single-image-account-type>
              </div>

              <div *ngSwitchCase="'meta-single-image-image'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-single-image-image
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-single-image-image>
              </div>

              <!-- ===== Meta Video Ad ===== -->
              <div *ngSwitchCase="'meta-video-headline'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-video-headline
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-video-headline>
              </div>

              <div *ngSwitchCase="'meta-video-primary-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-video-primary-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-video-primary-text>
              </div>

              <div *ngSwitchCase="'meta-video-cta-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-video-cta-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-video-cta-text>
              </div>

              <div *ngSwitchCase="'meta-video-url'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-video-url
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-video-url>
              </div>

              <div *ngSwitchCase="'meta-video-account-type'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-video-account-type
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-video-account-type>
              </div>

              <div *ngSwitchCase="'meta-video-video'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-video-video
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-video-video>
              </div>

              <!-- ===== Meta Carousel Ad ===== -->
              <div *ngSwitchCase="'meta-carousel-primary-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-carousel-primary-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-carousel-primary-text>
              </div>

              <div *ngSwitchCase="'meta-carousel-cta-text'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-carousel-cta-text
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-carousel-cta-text>
              </div>

              <div *ngSwitchCase="'meta-carousel-account-type'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-carousel-account-type
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-carousel-account-type>
              </div>

              <div *ngSwitchCase="'meta-carousel-card'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-meta-carousel-card
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-meta-carousel-card>
              </div>

              <!-- ===== Email blocks ===== -->
              <div *ngSwitchCase="'subject'">
                <app-subject
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-subject>
              </div>
              <div *ngSwitchCase="'preheader'">
                <app-preheader
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-preheader>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'counter'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-counter
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-counter>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'paragraph-cta'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-paragraphcta
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-paragraphcta>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'voucher'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-voucher
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-voucher>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'main-banner'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-main-banner
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-main-banner>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'gift-voucher'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-giftvoucher
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-giftvoucher>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'property-usp-2x-photo'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-propertyusp2xphoto
                  (mousedown)="$event.stopPropagation()"
                  *ngSwitchCase="'property-usp-2x-photo'"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-propertyusp2xphoto>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'zigzag-no-heading-no-button'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-zigzag-no-heading-no-button
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-zigzag-no-heading-no-button>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'zigzag-heading-and-cta'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-zigzag-heading-and-cta
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-zigzag-heading-and-cta>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'zigzag-heading-and-alt-cta'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-zigzag-heading-and-alt-cta
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-zigzag-heading-and-alt-cta>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'property-usp-1x-photo'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-propertyusp1xphoto
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-propertyusp1xphoto>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'property-description'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-property-description
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-property-description>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'x2-prop-rec'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-x2-prop-rec
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-x2-prop-rec>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'heading'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-heading
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-heading>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'reservation'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-reservation
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-reservation>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'event'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-event
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-event>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'experience-concierge'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-concierge
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-concierge>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'check-in'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-check-in
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-check-in>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'valamar-app'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-valamar-app
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-valamar-app>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'x2-experiences'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-x2-experiences
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-x2-experiences>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'x3-experiences'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-x3-experiences
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-x3-experiences>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'moments'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-moments
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-moments>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'inspiration'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-inspiration
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-inspiration>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'x2-prop-rec-onp'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-x2-prop-rec-onp
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-x2-prop-rec-onp>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'reviews'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-reviews
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-reviews>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'only-cta'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-only-cta
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-only-cta>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'disclaimer'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-disclaimer
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-disclaimer>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'only-intro'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-only-intro
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-only-intro>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>

              <div *ngSwitchCase="'only-2-photos'">
                <app-handle *ngIf="enableDragDrop"></app-handle>
                <app-only-2-photos
                  (mousedown)="$event.stopPropagation()"
                  [block]="selectedBlock"
                  (blockData)="onBlockDataUpdated($event)"
                  (removeBlockEvent)="removeBlock($event)"
                ></app-only-2-photos>
                <app-add-block
                  *ngIf="enableBlockToolbar && !isTranslator"
                  [currentUser]="currentUser"
                  [isNew]="isNew"
                  [lockedBy]="lockedBy"
                  [selectedBlocks]="selectedBlocks"
                  [blockSelect]="blockSelect"
                  [blockData]="selectedBlock"
                  (blockSelectEvent)="handleBlockChangeEvent($event)"
                ></app-add-block>
              </div>
              <div *ngSwitchDefault></div>
            </container-element>
          </div>
          <div class="mt-3 text-center" *ngIf="idcustom">
            <button
              type="submit"
              class="btn btn-primary modify-template-button"
              [disabled]="isSubmitDisabled || isReadonly()"
              *ngIf="!isTranslator"
            >
              <span *ngIf="!isCopy">{{
                  isNew ? "Create template" : "Update template"
                }}</span>
              <span *ngIf="isCopy">Confirm template copy</span>
            </button>
            <button
              [disabled]="isSubmitDisabled || isReadonly()"
              type="submit"
              class="btn btn-primary"
              *ngIf="isTranslator"
            >
              <span>Update translation</span>
            </button>
            <div>
              <button
                type="button"
                class="btn btn-primary bg-green-500 m-3"
                [ngStyle]="{ 'min-width': '20rem' }"
                *ngIf="isTranslator"
                (click)="confirmTranslating(confirmTranslatingModal)"
                [disabled]="isReadonly()"
              >
                <span>Finish translating</span>
              </button>
            </div>
          </div>
        </div>
      </form>

      <!-- START: Google preview pane -->
      <aside
        class="google-preview-pane"
        *ngIf="showSidePreview && selectedBlocks && selectedBlocks.length > 0"
      >
        <app-rsa-preview
          *ngIf="templateType === TypeEnum.GOOGLE_RSA_SEARCH"
          [blocks]="selectedBlocks"
          [language]="selectedLanguage"
        ></app-rsa-preview>

        <app-gen-single-image-preview
          *ngIf="templateType === TypeEnum.GOOGLE_GEN_SINGLE_IMAGE"
          [blocks]="selectedBlocks"
          [language]="selectedLanguage"
        ></app-gen-single-image-preview>

        <app-gen-video-preview
          *ngIf="templateType === TypeEnum.GOOGLE_GEN_VIDEO"
          [blocks]="selectedBlocks"
          [language]="selectedLanguage"
        ></app-gen-video-preview>

        <app-pmax-preview
          *ngIf="templateType === TypeEnum.GOOGLE_PERFORMANCE_MAX"
          [blocks]="selectedBlocks"
          [language]="selectedLanguage"
        ></app-pmax-preview>

        <app-youtube-video-preview
          *ngIf="templateType === TypeEnum.GOOGLE_YOUTUBE_VIDEO"
          [blocks]="selectedBlocks"
          [language]="selectedLanguage"
        ></app-youtube-video-preview>

        <app-meta-single-image-preview
          *ngIf="templateType === TypeEnum.META_SINGLE_IMAGE"
          [blocks]="selectedBlocks"
          [language]="selectedLanguage"
        ></app-meta-single-image-preview>

        <app-meta-video-preview
          *ngIf="templateType === TypeEnum.META_VIDEO"
          [blocks]="selectedBlocks"
          [language]="selectedLanguage"
        ></app-meta-video-preview>

        <app-meta-carousel-preview
          *ngIf="templateType === TypeEnum.META_CAROUSEL"
          [blocks]="selectedBlocks"
          [language]="selectedLanguage"
        ></app-meta-carousel-preview>
      </aside>
      <!-- END: Google preview pane -->
    </div>
  </div>
</div>
<ng-template #confirmTranslatingModal let-modal>
  <div class="modal-header">
    <h4 class="modal-title">Are you sure you are done translating?</h4>
    <button
      type="button"
      class="btn-close"
      aria-label="Close"
      (click)="modal.dismiss('Cross click')"
    ></button>
  </div>
  <div class="modal-body">
    <p>
      Once you confirm you are done, you will not be able to edit the template
      anymore.
    </p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-primary" (click)="finishTranslating()">
      Confirm
    </button>
    <button
      type="button"
      class="btn btn-secondary"
      (click)="modal.close('Close click')"
    >
      Close
    </button>
  </div>
</ng-template>
<app-heartbeat
  [idcustom]="idcustom || undefined"
  [lockedBy]="lockedBy.username || undefined"
  [type]="heartbeatType"
></app-heartbeat>
